import React, {useCallback, useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileImport, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import streetService from "../../../../services/web/street-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import StreetImport from "./components/street-import.jsx";

const initialFilter = {name: ""};

const StreetList = () => {
    const navigate = navigateHook();
    const [state, setState] = useState(initialFilter);
    const [filter, setFilter] = useState({});
    const [street, setStreet] = useState([]);
    const [importOpen, setImportOpen] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const gridRef = useRef(null);
    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const {isLoading, refetch} = useQuery({
        queryFn: () => streetService.list(filter).then(({data}) => {
            setStreet(data.data);
            return data;
        }),
        queryKey: ["street", filter],
    });

    const columnDefs = [
        {
            headerName: "Nome", field: "name", flex: 1, resizable: false,
            comparator: (a, b, nodeA, nodeB, isInverted) => aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted)
        }
    ];

    const onChange = ({target}) => {
        const {id, value} = target;
        setState(prev => ({...prev, [id]: value}));
    };

    const apply = useCallback(() => {
        gridRef.current?.api?.showLoadingOverlay();
        setFilter({name: state.name});
    }, [state]);

    const clearFilter = () => {
        setState(initialFilter);
        setFilter({});
    };

    const onCellClicked = ({data}) => navigate(`/street/form/${data.id}`, false);
    const newStreet = () => navigate("/street/form", false);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);

    return (
        <>
            <Template isLoading={isLoading || isImporting}>
                <Template.Header>
                    <Template.Title>Logradouros</Template.Title>
                    <div className="flex gap-2">
                        <Button target="success" onClick={() => setImportOpen(true)}>
                            <FaFileImport/> Importar
                        </Button>
                        <Button onClick={newStreet}>
                            <FaPlus/> Adicionar logradouro
                        </Button>
                    </div>
                </Template.Header>

                <Template.Body className="flex flex-col space-y-2">
                    <Card>
                        <Card.Body>
                            <div className="grid grid-cols-12">
                                <div className="col-span-12 flex gap-4">
                                    <InputForm classNameWrapper="w-full" id="name" value={state.name} onChange={onChange} label="Nome"/>
                                    <div className="flex gap-2">
                                        <Button title="Limpar" onClick={clearFilter} target="primary-outline"><FaEraser/></Button>
                                        <Button title="Buscar" onClick={apply}><FaSearch/></Button>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body className="flex">
                            <div className="h-[calc(100vh-153px)] w-full">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={street}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    gridOptions={aggridUtils.defaultGridOptions()}
                                    theme={theme}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>
            <StreetImport open={importOpen} setOpen={setImportOpen} refetch={refetch} setIsLoading={setIsImporting}/>
        </>
    );
};

export default StreetList;
